import type { GamePhase } from '../store/useGameStore';
import { findNextEnabledInCycle, type CycleAction } from './matchCycle';

/**
 * Machine à états « deux équipes » (VS-12 — Lot 1).
 *
 * Le mode deux équipes réutilise **la même** machine de cycle intra-équipe que le
 * mode une équipe (`AFTER_ACTION_TARGET` dans `matchCycle.ts`) et n'ajoute qu'une
 * seule notion : une **équipe active** (`home`/`away`) qui **bascule sur les
 * actions traversant le filet**. Toute la logique de flip est centralisée ici —
 * la liste des actions traversantes ne doit exister qu'à cet endroit.
 */

export type TeamSide = 'home' | 'away';

/**
 * Seules actions qui traversent le filet → bascule de l'équipe active.
 * (Les autres — Réception, Passe, Bloc, Défense, Freeball Reçue — restent dans
 * la même équipe.)
 */
export const TRAVERSING_ACTIONS: readonly string[] = [
  'Service',
  'Attaque',
  'Freeball Donnée',
  'Retour Direct',
];

const TRAVERSING_SET: ReadonlySet<string> = new Set(TRAVERSING_ACTIONS);

export function isTraversingAction(action: string): boolean {
  return TRAVERSING_SET.has(action);
}

/**
 * Gestes défensifs dont la qualité « / » (« Directe ») renvoie le ballon DANS LE
 * CAMP D'EN FACE : une réception, une défense ou une freeball reçue qui repart
 * directement par-dessus le filet.
 *
 * Le ballon ne traverse alors pas à cause du NOM de l'action mais de sa QUALITÉ,
 * et l'équipe d'en face récupère une balle facile. Sans cette exception, la
 * saisie deux équipes restait du même côté et proposait un bloc — alors que
 * personne n'attaque : le ballon vient d'arriver chez l'adversaire.
 *
 * `Bloc`, `Attaque` and `Retour Direct` are left out on purpose: their "/" ends
 * the rally (invasion for the block, VOL-189; stuffed attack otherwise).
 */
const DIRECT_RETURN_ACTIONS: ReadonlySet<string> = new Set([
  'Réception',
  'Défense',
  'Freeball Reçue',
]);

export function isDirectReturn(action: string, quality?: string): boolean {
  return quality === '/' && DIRECT_RETURN_ACTIONS.has(action);
}

export function flipSide(side: TeamSide): TeamSide {
  return side === 'home' ? 'away' : 'home';
}

/**
 * Équipe active au tout début d'un rallye : celle qui sert.
 * Invariant VS-12 : notre équipe = `home` (`servingSide 'us'`), adversaire = `away`.
 */
export function initialActiveTeam(servingSide: 'us' | 'them'): TeamSide {
  return servingSide === 'us' ? 'home' : 'away';
}

/**
 * Équipe active **après** une action commise par `active` : bascule si l'action
 * traverse le filet, sinon reste la même équipe.
 */
export function advanceActiveTeam(active: TeamSide, action: string): TeamSide {
  return isTraversingAction(action) ? flipSide(active) : active;
}

/**
 * Geste du rallye pour la dérivation de l'équipe active.
 *
 * `blockReturnsBall` couvre la seule traversée qui ne se lit pas sur le nom de
 * l'action : un `Bloc` qui renvoie le ballon dans le camp de l'attaquant (cible
 * de l'attaque marquée `BLOCKED`), après quoi c'est la couverture de l'attaquant
 * qui joue. Les appelants qui n'ont que des noms d'actions passent des chaînes.
 */
export interface DualRallyStep {
  action: string;
  blockReturnsBall?: boolean;
  /** Qualité saisie : un « / » sur un geste défensif renvoie le ballon en face. */
  quality?: string;
}

function stepAction(step: string | DualRallyStep): string {
  return typeof step === 'string' ? step : step.action;
}

/** Équipe active après un geste, exception du contre renvoyé comprise. */
export function advanceActiveTeamForStep(active: TeamSide, step: string | DualRallyStep): TeamSide {
  if (typeof step !== 'string') {
    if (step.action === 'Bloc' && step.blockReturnsBall) {
      return flipSide(active);
    }
    if (isDirectReturn(step.action, step.quality)) {
      return flipSide(active);
    }
  }
  return advanceActiveTeam(active, stepAction(step));
}

/**
 * Recalcule l'équipe active à partir du côté au service et de la suite d'actions
 * déjà saisies dans le rallye courant. Base du rétablissement cohérent après
 * undo / suppression (pas de dérive d'état).
 */
export function deriveActiveTeam(
  servingSide: 'us' | 'them',
  actions: readonly (string | DualRallyStep)[],
): TeamSide {
  return actions.reduce<TeamSide>(
    (side, step) => advanceActiveTeamForStep(side, step),
    initialActiveTeam(servingSide),
  );
}

/**
 * Action d'entrée de l'équipe qui récupère le ballon juste après une action
 * traversante (utilisé pour suggérer la prochaine action en saisie deux équipes).
 */
const TRAVERSING_ENTRY: Record<string, 'Réception' | 'Freeball Reçue' | CycleAction> = {
  Service: 'Réception',
  'Freeball Donnée': 'Freeball Reçue',
  Attaque: 'Bloc',
  'Retour Direct': 'Bloc',
};

/**
 * Transition suggérée **après une action traversante** en mode deux équipes :
 * on passe la main à l'équipe adverse sur son action d'entrée (réception d'un
 * service / d'une freeball, bloc-défense d'une attaque). Les transitions
 * intra-équipe (non traversantes) restent gérées par `getNextTransition`
 * (`AFTER_ACTION_TARGET`), inchangé.
 */
export function nextDualTransition(
  traversingAction: string,
  enabledActions: Set<string>,
  quality?: string,
): { action: string; phase: GamePhase } {
  // Renvoi direct : l'équipe d'en face reçoit une balle facile, pas une attaque.
  // Lui proposer un bloc n'aurait aucun sens — il n'y a rien à bloquer.
  if (isDirectReturn(traversingAction, quality)) {
    return { action: 'Freeball Reçue', phase: 'Dig' };
  }
  const entry = TRAVERSING_ENTRY[traversingAction] ?? 'Bloc';
  if (entry === 'Réception') return { action: 'Réception', phase: 'Reception' };
  if (entry === 'Freeball Reçue') return { action: 'Freeball Reçue', phase: 'Dig' };
  // Attaque / Retour Direct → l'équipe adverse défend (bloc puis, sinon, défense).
  const next = findNextEnabledInCycle(entry, enabledActions);
  return { action: next.action, phase: next.phase };
}
